import {
  type CreateManualReviewResolutionApprovalCommand,
  isCreateManualReviewResolutionApprovalCommand,
} from "@billiard/contracts";
import { manualReviewResolutionOutcomeIsUnknown } from "./api";

export interface ManualReviewResolutionRecovery {
  ownerIdentityKey: string;
  command: CreateManualReviewResolutionApprovalCommand;
  state: "CLAIMED" | "OUTCOME_UNKNOWN";
}

export type ManualReviewResolutionRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: ManualReviewResolutionRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE" };

export const MANUAL_REVIEW_RESOLUTION_RECOVERY_STORAGE_KEY =
  "billiard-erp:manual-review-resolution-recovery:v1";

let pending: ManualReviewResolutionRecovery | null = null;
let dispatching: ManualReviewResolutionRecovery | null = null;
let hydrated = false;
let unavailable = false;

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key))
  );
}

function bounded(value: unknown, maximum: number): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    [...value].length <= maximum
  );
}

export function manualReviewResolutionIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function validIdentityKey(value: unknown): value is string {
  if (!bounded(value, 1_024)) return false;
  try {
    const parts = JSON.parse(value) as unknown;
    return Array.isArray(parts) && parts.length === 3 && parts.every((part) => bounded(part, 512));
  } catch {
    return false;
  }
}

function frozen(
  ownerIdentityKey: string,
  command: CreateManualReviewResolutionApprovalCommand,
  state: ManualReviewResolutionRecovery["state"],
): ManualReviewResolutionRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
  });
}

function parsed(value: unknown): ManualReviewResolutionRecovery | null {
  if (
    !record(value) ||
    !exactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !record(value.recovery)
  ) {
    return null;
  }
  const recovery = value.recovery;
  if (
    !exactKeys(recovery, ["ownerIdentityKey", "command", "state"]) ||
    !validIdentityKey(recovery.ownerIdentityKey) ||
    !isCreateManualReviewResolutionApprovalCommand(recovery.command) ||
    (recovery.state !== "CLAIMED" && recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return frozen(recovery.ownerIdentityKey, recovery.command, recovery.state);
}

function same(
  left: ManualReviewResolutionRecovery,
  right: ManualReviewResolutionRecovery,
): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function readStored(target: Storage): ManualReviewResolutionRecovery | null {
  const encoded = target.getItem(MANUAL_REVIEW_RESOLUTION_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : parsed(JSON.parse(encoded) as unknown);
}

function save(recovery: ManualReviewResolutionRecovery): boolean {
  if (typeof window === "undefined") return true;
  const target = storage();
  if (!target) return false;
  try {
    target.setItem(
      MANUAL_REVIEW_RESOLUTION_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const stored = readStored(target);
    return stored !== null && same(stored, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const target = storage();
  if (!target) {
    unavailable = true;
    return;
  }
  try {
    const encoded = target.getItem(MANUAL_REVIEW_RESOLUTION_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    const stored = readStored(target);
    if (!stored) {
      unavailable = true;
      return;
    }
    const recovery =
      stored.state === "CLAIMED"
        ? frozen(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !save(recovery)) {
      unavailable = true;
      return;
    }
    pending = recovery;
  } catch {
    unavailable = true;
  }
}

export function readManualReviewResolutionRecovery(
  identityKey: string,
): ManualReviewResolutionRecoveryView {
  hydrate();
  if (unavailable) return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  if (!pending) return { status: "NONE" };
  return pending.ownerIdentityKey === identityKey
    ? { status: "OWNED", recovery: pending }
    : { status: "OTHER_OPERATOR" };
}

export function claimManualReviewResolutionRecovery(
  identityKey: string,
  command: CreateManualReviewResolutionApprovalCommand,
): ManualReviewResolutionRecoveryView {
  const current = readManualReviewResolutionRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!validIdentityKey(identityKey) || !isCreateManualReviewResolutionApprovalCommand(command)) {
    unavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  const recovery = frozen(identityKey, command, "CLAIMED");
  if (!save(recovery)) {
    unavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE" };
  }
  pending = recovery;
  return { status: "OWNED", recovery };
}

function exactDurable(recovery: ManualReviewResolutionRecovery, identityKey: string): boolean {
  hydrate();
  if (
    unavailable ||
    pending !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    (typeof window !== "undefined" && !storage())
  ) {
    unavailable = true;
    return false;
  }
  if (typeof window === "undefined") return true;
  try {
    const target = storage();
    const stored = target ? readStored(target) : null;
    if (stored && same(stored, recovery)) return true;
  } catch {
    // Fall through to the fail-closed result.
  }
  unavailable = true;
  return false;
}

export function beginManualReviewResolutionDispatch(
  recovery: ManualReviewResolutionRecovery,
  identityKey: string,
): boolean {
  if (dispatching || !exactDurable(recovery, identityKey)) return false;
  dispatching = recovery;
  return true;
}

export function finishManualReviewResolutionDispatch(
  recovery: ManualReviewResolutionRecovery,
  identityKey: string,
): boolean {
  if (dispatching !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatching = null;
  return true;
}

export function markManualReviewResolutionOutcomeUnknown(
  recovery: ManualReviewResolutionRecovery,
  identityKey: string,
): boolean {
  if (!exactDurable(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : frozen(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!save(unknown)) {
    unavailable = true;
    return false;
  }
  pending = unknown;
  return true;
}

export function clearManualReviewResolutionRecovery(
  recovery: ManualReviewResolutionRecovery,
  identityKey: string,
): boolean {
  if (!exactDurable(recovery, identityKey)) return false;
  if (typeof window !== "undefined") {
    const target = storage();
    if (!target) {
      unavailable = true;
      return false;
    }
    try {
      target.removeItem(MANUAL_REVIEW_RESOLUTION_RECOVERY_STORAGE_KEY);
      if (target.getItem(MANUAL_REVIEW_RESOLUTION_RECOVERY_STORAGE_KEY) !== null) {
        unavailable = true;
        return false;
      }
    } catch {
      unavailable = true;
      return false;
    }
  }
  pending = null;
  return true;
}

export function manualReviewResolutionRecoveryOutcomeIsUnknown(error: unknown): boolean {
  return manualReviewResolutionOutcomeIsUnknown(error);
}

export function resetManualReviewResolutionRecoveryForTests(): void {
  pending = null;
  dispatching = null;
  hydrated = false;
  unavailable = false;
  try {
    storage()?.removeItem(MANUAL_REVIEW_RESOLUTION_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup resets the in-memory fail-closed state as well.
  }
}

export function resetManualReviewResolutionRecoveryMemoryForTests(): void {
  pending = null;
  dispatching = null;
  hydrated = false;
  unavailable = false;
}
